<template>
  <div @click="clearContext" v-if="$store.isLogin">
    <Logo />
    <div class="tree-search-content">
      <div class="tc-15-search tc-15-multi-search">
        <div class="search-input-wrap">
          <input class="tc-15-search-words" type="text" ref="searchInput" v-model="keyword"
            @keyup.enter="onSearch" :placeholder="$t('core.search') + ' ' + (searchDir || Bucket || '')">
          <a v-if="keyword" href="#" role="button" class="btn-rm-txt" :title="$t('core.clear')"
            @click="closeSearch">{{$t('core.clear')}}</a>

          <qc-bubble type="dark" :vertical-offset="-14">
            <button class="tc-15-btn weak m search" :class="keyword ? 'search-btn-border':''"
              @click="onSearch"></button>
            <template slot="popup">{{$t('core.search')}}</template>
          </qc-bubble>
        </div>
        <div class="ml-5">
          <qc-bubble type="dark">
            <button type="button" class="tc-15-btn weak m refresh m-0"
              @click="onRefresh">{{$t('core.refresh')}}</button>
            <template slot="popup">{{$t('core.refresh')}}</template>
          </qc-bubble>
        </div>
      </div>
    </div>
    <div class="tc-action-grid search-bar" v-if="(isSearching || isSearchDone) && showSearchBar">
      <div class="justify-grid">
        <template>
          <div class="col">
            <span class="search-text">{{$t('core.search_result[4]',
              {num: searchedLength,
              result: beyondSearchLimit ? searchLimitStr : searchResultList.length,
              })}}</span>
          </div>
          <div class="col">
            <a v-if="isSearched && isSearching" href="javascript:;" class="cancel-search"
              @click="cancelSearch">{{$t('core.cancel')}}</a>
            <a v-else href="javascript:;" class="cancel-search" @click="closeSearch">{{$t('core.close')}}</a>
          </div>
        </template>
      </div>
    </div>
    <!-- 树start -->
    <div class="tree-content">
      <qc-tree :lazy="lazy" :data="list" :loading="loading || isSearching"
      :load="loadNode" :load-more="loadNode" :has-more="hasMore"
        @node-click="nodeClick" @contextmenu="contextmenuFn"
        ref="tree">
        <template class="self-tree-node-icon" slot="icon" slot-scope="{ node }">
          <i v-if="!node.isLeaf" class="cos-icon--32" :class="node.name | FileIcon(node.isDir)"></i>
          <i v-else class="cos-icon--32" :class="node.name | FileIcon(node.isDir)"></i>
        </template>
        <template slot="empty-tips">
            <div v-if="!hasAuthority" class="qc-tree-loading">
              {{$t('cos.tips.no_permission')}}
            </div>
            <div v-else-if="getBucketErrorMsg" class="qc-tree-loading">
              {{getBucketErrorMsg}}
            </div>
            <div v-else>
              <div v-if="loading" class="qc-tree-loading">
                <i class="n-loading-icon"></i>
                {{$t('cos.tips.loading')}}
              </div>
              <div v-else-if="!loading && list.length===0" class="qc-tree-loading">
                {{$t('cos.tips.list_empty')}}
              </div>
            </div>
          </template>
      </qc-tree>
    </div>
    <!-- 树end -->

    <!-- 操作相关 start -->
    <div class="operate-content">
      <ul>
        <li><a href="javascript:;" @click="toStandardMode">{{$t('core.standard_mode')}}</a></li>
        <li><a href="javascript:;" @click="logout">{{$t('core.logout')}}</a></li>
      </ul>
    </div>
    <!-- 操作相关 end -->

    <!-- 文件操作 -->
    <create-dialog ref="create" :bucket-info="{...bucketInfo,Prefix: createRealPrefix}"
      @finish-create="(name, isFolder) => createFolderFn(name, isFolder)">
    </create-dialog>
    <advance-upload ref="advanceUpload" :show.sync="dialogShow.advanceUpload"
      :bucket-info="{...bucketInfo,Prefix: realPrefix}" :allowCAS="allowCASMap[Region]"
      :allowDeepCAS="allowDeepCASMap[Region]"></advance-upload>
    <delete-file :show.sync="dialogShow.delete" :list.sync="list" :selected-list="selectedList"
      :bucket-info="bucketInfo" :enableReplication="enableReplication" @fix-folder="fixFolder(Prefix)"
      @refresh="onRefresh" @deleteFn="deleteFn">
    </delete-file>
    <rename-file :show.sync="dialogShow.renameFile" :bucket-info="{...bucketInfo,Prefix}" :rename-item="selectedList[0]"
      @refresh="onRenameFinish">
    </rename-file>
    <share-file ref="shareFile" :show.sync="dialogShow.shareFile" :share-link-info.sync="shareFileInfo"
      :bucket-info="{...bucketInfo,Prefix}"></share-file>
    <share-multiple :show.sync="dialogShow.shareMultiple" :selected-list="selectedList"
      :allowShare="allowUploadMap[Region]" @submit="handleShareMultiple">
    </share-multiple>
    <share-result :show.sync="dialogShow.shareResult" :share-multiple-info="shareMultipleInfo"></share-result>
    <context-menu :show.sync="dialogShow.contextMenu" :position="contextMenuPosition" :menu-type="menuType"
      :show-icon="false" @on-action="handleEventAction"></context-menu>
    <div v-show="0">
      <!-- 下载使用 iframe -->
      <iframe id="downloadCosFile" src="" name="download" height="0" width="0"></iframe>
      <!-- 文件上传 -->
      <input ref="fileInput" type="file" multiple="multiple" @change="handleFileOrFolderInput($event, 'file')">
      <!-- 文件夹上传 -->
      <input ref="directoryInput" type="file" multiple="multiple" webkitdirectory="webkitdirectory"
        @change="handleFileOrFolderInput($event, 'folder')">
    </div>
  </div>
</template>

<script>
import {
  tips,
  formatSize,
  getFileIcon,
  formatTime,
  downloadUrl,
  path as pathLib,
} from '@/scripts/tools/utils';
import cos from '@/scripts/helper/cos';
import cosExpand from '@/scripts/helper/cos/cosExpand';
import { config, regions } from '@/scripts/tools/config';
import eventProxy from '@/scripts/tools/eventProxy';
import mta from '@/scripts/common/mta';
import * as cosSdkUtils from '@/scripts/helper/cos/cosSdkUtils';
import copyer from '@/scripts/helper/copyer';
import downloader from '@/scripts/helper/downloader';
import shareMultipleTools from '@/scripts/helper/shareMultipleTools';

import createDialog from '@/components/dialog/file/createFileOrFolder.vue';
import advanceUpload from '@/components/dialog/file/advanceUpload.vue';
import deleteFile from '@/components/dialog/file/deleteFile.vue';
import renameFile from '@/components/dialog/file/renameFile.vue';
import shareFile from '@/components/dialog/file/share/shareFile.vue';
import shareMultiple from '@/components/dialog/file/share/shareMultiple.vue';
import shareResult from '@/components/dialog/file/share/shareResult.vue';
import contextMenu from '@/components/common/contextMenu.vue';

import QcTree from '@/components/common/tree/tree.vue';
import Logo from './Logo.vue';

export default {
  components: {
    Logo,
    QcTree,
    createDialog,
    advanceUpload,
    deleteFile,
    renameFile,
    shareFile,
    shareMultiple,
    shareResult,
    contextMenu,
  },
  props: {
    fileItem: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      lazy: true,
      loading: false,
      list: [],
      hasMore: false,
      Prefix: '',
      Marker: '',
      hasAuthority: true,
      getBucketErrorMsg: '',

      // 桶信息
      Bucket: '',
      Region: '',
      bucketInfo: {}, // 存储桶详情信息
      shareFileInfo: {}, // 文件分享信息
      shareMultipleInfo: {}, // 多文件分享信息

      // 地域支持信息
      allowCIMap: {}, // 支持万象地域
      allowCASMap: {}, // 支持归档存储地域
      allowDeepCASMap: {}, // 支持深度归档存储地域
      allowUploadMap: {}, // 支持文件上传地域

      // 搜索框
      keyword: '',
      searchDir: '',
      searchTypeShowStr: 'fuzzy', // dropmenu 展示用
      searchType: 'fuzzy', // 真正搜索使用
      isSearching: false,
      isSearched: false,
      isSearchDone: false,
      searchedLength: 0,
      beyondSearchLimit: false,
      showSearchBar: true,
      searchResultList: [],

      // 弹窗
      dialogShow: {
        advanceUpload: false,
        contextMenu: false,
        delete: false,
        fileInfo: false,
        renameFile: false,
        openCosBrowser: false, // 打开 COSBrowser 客户端弹窗
        loadCosBrowser: false, // 加载 COSBrowser 客户端弹窗
        shareFile: false,
        shareMultiple: false,
        shareResult: false,
      },

      // 右键弹窗
      menuType: '',
      contextMenuPosition: {},
      disabledBtn: {
        bucketInfo: true,
        upload: true,
        download: true,
        delete: true,
        create: true,
        copy: true,
        paste: true,
        edit: true,
        preview: true,
      },
      // 准备右键操作的节点
      contextingNode: null,
      selectedList: [],
      enableReplication: false,

      // 每个前缀存有hasmore和marker
      prefixMarker: {},
    };
  },
  watch: {
  },
  computed: {
    searchBarExist() {
      return this.isSearching || this.isSearchDone;
    },
    searchLimitStr() {
      return this.list.length === 0 ? 0 : `${config.searchLimit - 1}+`;
    },
    realKeyword() {
      return this.keyword;
    },
    realSearchType() {
      return this.searchType;
    },
    realPrefix() {
      if (!this.selectedList.length) {
        return this.Prefix;
      }
      return this.selectedList[0].key;
    },
    createRealPrefix() {
      // 创建文件/文件夹时所需前缀
      if (!this.selectedList.length) {
        return this.Prefix;
      }
      return this.selectedList[0].isDir ? this.selectedList[0].key : '';
    },
  },
  created() {
    this.setData();
    this.initTree();
    eventProxy.on('upload-completed', this.onUploadFinish);
    eventProxy.on('command.copy', this.copyFiles);
    eventProxy.on('command.paste', this.pasteFiles);
    eventProxy.on('command.refresh', this.onRefresh);
    eventProxy.on('saveAs', this.appendTreeNode);
  },
  mounted() {
  },
  destroyed() {
    eventProxy.off('upload-completed', this.onUploadFinish);
    eventProxy.off('command.copy', this.copyFiles);
    eventProxy.off('command.paste', this.pasteFiles);
    eventProxy.off('command.refresh', this.onRefresh);
    eventProxy.off('saveAs', this.appendTreeNode);
  },
  methods: {
    async setData() {
      const { bucket, region, path = '' } = this.$route.query;
      this.Bucket = bucket;
      this.Region = region;
      const prefix = path.replace(/\/[^/]+$/, '/');
      this.Prefix = prefix;
      this.prefixMarker[prefix] = {
        hasMore: false,
        marker: '',
      };
      const bucketInfo = await cosExpand.getBucketInfo({
        Bucket: bucket,
        Region: region,
      });
      this.bucketInfo = { ...bucketInfo, Bucket: bucket, Region: region };
      this.getRemoteConfig();
    },
    getRemoteConfig() {
      regions.forEach(item => {
        this.allowCIMap[item.region] = !item.disable_CI || false;
        this.allowCASMap[item.region] = !item.disable_CAS || false;
        this.allowDeepCASMap[item.region] = !item.disable_deep_CAS;
        this.allowUploadMap[item.region] = !item.disable_upload;
      });
    },
    async getTreeData(prefix) {
      const marker = this.prefixMarker[prefix]?.marker || '';
      const res = await this.pullList(prefix, marker);
      if (res && prefix) {
        if (!this.prefixMarker[prefix]) {
          this.prefixMarker[prefix] = {};
        }
        this.prefixMarker[prefix].hasMore = res.hasMore;
        this.prefixMarker[prefix].marker = res.marker;
      }
      return res;
    },
    async initTree() {
      this.lazy = true;
      this.loading = true;
      const res = await this.getTreeData(this.Prefix);
      this.loading = false;
      this.list = res.list;
      this.hasMore = res.hasMore;
      this.Marker = res.marker;
    },
    async searchTree() {
      const res = await this.getTreeData(this.Prefix + this.keyword);
      this.list = res.list;
      this.hasMore = res.hasMore;
      this.Marker = res.marker;
    },
    // 获取文件列表数据
    async pullList(Prefix, Marker) {
      this.getBucketErrorMsg = '';
      return new Promise((resolve, reject) => {
        const self = this;
        const { Bucket, Region } = this;
        if (!Bucket) {
          tips.error(window.lang.t('error.bucket_not_selected'));
          reject();
          return;
        }
        // 若为前缀搜索且关键字不为空，则修改搜索状态
        if (this.realSearchType === 'prefix') {
          if (this.keyword) {
            this.isSearching = true;
          }
        }
        cos.getBucket(
          {
            Bucket,
            Region,
            Prefix,
            Marker,
            Delimiter: '/',
            MaxKeys: 1000,
          },
          async (err, data) => {
            tips.closeTips();
            if (Bucket !== self.Bucket || Region !== self.Region) {
              reject();
              return;
            }
            if (err) {
              const error = (err && err.error) || err;
              let errorMsg = '';
              if (error) {
                errorMsg = error.Code || error.code || error.Message || error;
              }
              this.getBucketErrorMsg = window.lang.err(`error['${errorMsg}']`, error);
              tips.error(this.getBucketErrorMsg);
              if (errorMsg === 'AccessDenied') self.hasAuthority = false;
              // 若为前缀搜索且关键字不为空，则修改搜索状态
              if (this.realSearchType === 'prefix' && this.keyword) {
                this.isSearching = false;
              }
              reject();
              return;
            }
            const hasMore = data.IsTruncated === 'true';
            const marker = data.NextMarker;
            const list = await this.handleFileList(data);
            // 若为前缀搜索且关键字不为空，则修改搜索状态
            if (this.realSearchType === 'prefix' && this.keyword) {
              this.isSearching = false;
              this.isSearched = true;
              this.isSearchDone = true;
            }
            resolve({ hasMore, marker, list });
          },
        );
      });
    },
    async handleFileList(data) {
      this.hasMore = data.IsTruncated === 'true';
      this.Marker = data.NextMarker;
      const list = this.formatFileListData(data);
      let newList = [];
      if (this.realSearchType === 'prefix' && this.keyword) {
        newList = await this.arrangeList(list, data.Prefix);
      } else {
        newList = await this.pushList(list, data.Prefix);
      }
      return newList;
    },
    formatFileListData(data) {
      const list = [];
      if (data.CommonPrefixes && data.CommonPrefixes.length > 0) {
        const CommonPrefixes = data.CommonPrefixes.filter(
          v => v.Key !== data.Prefix,
        ); // 过滤出目录
        list.push(...CommonPrefixes.map(this.formatFileItem));
      }
      if (data.Contents && data.Contents.length > 0) {
        const Contents = data.Contents.filter(v => !v.Key.endsWith('/')); // 过滤出文件
        list.push(...Contents.map(this.formatFileItem));
      }
      return list;
    },
    // 增加文件/文件夹
    async pushList(list, prefix) {
      let i = list.length;
      let item = {};
      let index = 0;
      let relKey = '';
      let dirKey = '';
      const deepMap = {};
      const deepItems = [];
      let newList = [];
      while (--i >= 0) {
        item = list[i];
        if (item.key.indexOf(prefix) === 0) {
          relKey = item.key.substr(prefix.length);
          if (item.isDir) {
            relKey = relKey.slice(0, -1);
          }
          index = relKey.indexOf('/');
          if (index > -1) {
            dirKey = `${prefix + relKey.substr(0, index)}/`;
            if (!deepMap[dirKey]) {
              deepItems.push(this.formatFileItem({ Prefix: dirKey }));
              deepMap[dirKey] = true;
            }
            list.splice(i, 1);
          }
        } else {
          list.splice(i, 1);
        }
      }
      list.push(...deepItems);
      newList = await this.arrangeList(list);
      return newList;
    },
    // 整理列表，去重，排序
    async arrangeList(list) {
      // 从后往前去重
      let i = list.length;
      const hasNodeId = {};
      while (--i >= 0) {
        if (hasNodeId[list[i].key]) {
          list.splice(i, 1);
        }
        hasNodeId[list[i].key] = true;
      }
      // 排序
      const newList = await this.sortList(list);
      return newList;
    },
    async sortList(list) {
      const sortedList = list.sort((a, b) => {
        let com = 0;
        if (a.isDir !== b.isDir) {
          com = a.isDir ? -1 : 1;
        } else {
          com = a.name > b.name ? 1 : -1;
        }
        return com;
      });
      return sortedList;
    },
    // 整理列表格式
    formatFileItem(item) {
      let o;
      if (item.Prefix) {
        let name = item.Prefix.slice(0, -1);
        name = name.substr(name.lastIndexOf('/') + 1);
        const _locates = item.Prefix.split('/');
        const len = _locates.length;
        o = {
          key: item.Prefix,
          name,
          label: name,
          isShared: this.shareMap && !!this.shareMap[item.Prefix],
          isDir: true,
          isLeaf: false,
          location: _locates.length > 2 ? _locates[len - 3] : this.Bucket, // 所在目录
          size: 0,
          sizeText: '',
          icon: 'document',
          mtime: '-',
          previewUrl: '',
          ETag: '',
          StorageClass: '',
        };
      } else {
        const _locates = item.Key.split('/');
        const len = _locates.length;
        o = {
          key: item.Key,
          name: item.Key.substr(item.Key.lastIndexOf('/') + 1),
          label: item.Key.substr(item.Key.lastIndexOf('/') + 1),
          isDir: false,
          isLeaf: true,
          location: _locates.length > 1 ? _locates[len - 2] : this.Bucket, // 所在目录
          size: parseInt(item.Size, 10),
          sizeText: item.Size ? formatSize(item.Size, 2) : '-',
          icon: getFileIcon(item.Key),
          mtime: item.LastModified ? formatTime(item.LastModified) : '-',
          previewUrl: '',
          ETag: item.ETag,
          StorageClass: item.StorageClass,
        };
      }
      o.url = cos.getObjectUrl({
        Bucket: this.Bucket,
        Region: this.Region,
        Key: o.key,
        Sign: false,
      });
      o.itemClass = 'finder-item';
      o.checked = false;
      return o;
    },

    // 搜索
    changeSearchType(searchType) {
      this.searchTypeShowStr = searchType;
    },
    onSearch() {
      if (!this.keyword) {
        tips.error(this.$t('core.keyword_empty'));
        return;
      }
      this.searchType = this.searchTypeShowStr;
      mta.clickStat('search', { searchtype: this.realSearchType });

      this.list.splice(0);
      this.searchResultList.splice(0);
      this.Marker = '';
      this.lazy = false;
      this.showSearchBar = true;

      const self = this;

      [this.loading, this.isSearching, this.isSearched] = [true, true, false];
      this.hasMore = false;
      this.searchedLength = 0;
      this.beyondSearchLimit = false;

      // 取消之前的搜索迭代
      let canceled = false;
      eventProxy.emit('cancel-last-search');

      const { Bucket, Region, keyword } = this;
      let params = {
        Bucket,
        Region,
        MaxKeys: 1000,
        // Delimiter: '/',
      };
      const list = [];
      const dirMap = {};

      const next = (Prefix, Marker) => {
        if (canceled) return;
        // 监听是否取消本次搜索事件
        eventProxy.once('cancel-last-search', () => {
          canceled = true;
          self.formatSearchResult();
        });

        params = { ...params, Prefix, Marker };
        cos.getBucket(params, async (err, data) => {
          if (Bucket !== self.Bucket || Region !== self.Region) return;
          if (err) {
            let error = (err && err.error) || err;
            if (error) {
              error = error.Code || error.code || error.Message;
            }
            tips.error(window.lang.err(`error['${error}']`), error);
            self.loading = false;
            self.isSearching = false;
            return;
          }
          if (self.isSearching && data && !canceled) {
            if (data.Contents && data.Contents.length) {
              let Dirs = [];
              const Contents = data.Contents.filter(item => {
                self.searchedLength++;

                // 忽略前缀表示的文件（or文件夹）
                if (item.Key === data.Prefix) return false;

                // 搜到的 key 是真实目录
                if (item.Key.slice(-1) === '/') {
                  const keys = item.Key.split('/');
                  keys.length -= 1;
                  let i = 0;
                  for (; i < keys.length; i++) {
                    if (keys[i].includes(keyword)) {
                      const index = i;
                      const dir = keys
                        .slice(0, index + 1)
                        .map(key => `${key}/`)
                        .join('');
                      if (dir !== data.Prefix && !dirMap[dir]) {
                        dirMap[dir] = true;
                      }
                    }
                  }
                  return false;
                }
                // 搜到的 key 非真实目录
                const keys = item.Key.split('/');
                let i = 0;
                for (; i < keys.length; i++) {
                  if (keys[i].includes(keyword)) {
                    if (i !== keys.length - 1) {
                      // 虚拟目录
                      const index = i;
                      const dir = keys
                        .slice(0, index + 1)
                        .map(key => `${key}/`)
                        .join('');
                      if (dir !== data.Prefix && !dirMap[dir]) {
                        dirMap[dir] = true;
                      }
                    }
                    // 文件
                    return keys[i].includes(keyword);
                  }
                }
                return false;
              });
              Dirs = Object.keys(dirMap).map(item => ({ Prefix: item }));
              list.push(...Dirs.map(self.formatFileItem));
              list.push(...Contents.map(self.formatFileItem));
            }
            if (list.length) {
              self.isSearched = true;
              const newList = [...self.searchResultList, ...list];
              this.searchResultList = await self.arrangeList(newList, data.Prefix);
            }
            if (
              data.IsTruncated === 'true'
              && self.searchResultList.length < config.searchLimit
            ) {
              self.beyondSearchLimit = false;
              self.loading = false;
              next(data.Prefix, data.NextMarker);
            } else {
              // 当检索出的数据达到searchLimit就停止拉取数据
              if (self.searchResultList.length >= config.searchLimit) {
                self.beyondSearchLimit = true;
                self.searchResultList.splice(
                  config.searchLimit,
                  self.searchResultList.length - config.searchLimit,
                );
              }
              self.formatSearchResult();
              [self.hasMore, self.loading] = [false, false];
              [self.isSearching, self.isSearched, self.isSearchDone] = [
                false,
                true,
                true,
              ];
            }
          }
        });
      };
      next(this.Prefix, this.Marker);
    },
    formatSearchResult() {
      const dirMap = {
        dirTotal: {},
        dirs: [],
        files: [],
      };
      this.searchResultList.forEach(item => {
        let dirKey = item.key.replace(`${this.Prefix}`, '');
        if (item.isDir || (!item.isDir && dirKey.includes('/'))) {
          // 目录或带目录前缀的文件
          if (!item.isDir) {
            const dirs = dirKey.split('/');
            dirs.pop();
            dirKey = dirs.map(dir => `${dir}/`).join('');
          }
          if (!dirMap.dirTotal[dirKey]) {
            dirMap.dirTotal[dirKey] = {
              children: [],
            };
          }
          if (!item.isDir) {
            dirMap.dirTotal[dirKey].children.push(item);
          }
        } else {
          // 单文件
          dirMap.files.push(item);
        }
      });
      const loop = (item, index) => {
        const restDirs = item
          ? Object.keys(dirMap.dirTotal).filter(m => m.startsWith(item.Prefix)).sort()
          : Object.keys(dirMap.dirTotal).sort();
        if (restDirs.length === 0) {
          return;
        }
        const restExistDirs = [];
        restDirs.forEach(m => {
          const currentPrefix = `${m.split('/')[index]}/`;
          if (!restExistDirs.includes(currentPrefix)) {
            restExistDirs.push(currentPrefix);
            const prefix = item ? item.Prefix + currentPrefix : currentPrefix;
            let dirInfo = {
              Prefix: prefix,
            };
            const moreInfo = this.formatFileItem(dirInfo);
            dirInfo = {
              ...dirInfo,
              ...moreInfo,
            };
            if (item && !item.children) {
              item.children = [];
            }
            if (dirMap.dirTotal[prefix]) {
              dirInfo.children = dirMap.dirTotal[prefix].children;
              delete dirMap.dirTotal[prefix];
            }
            if (item) {
              item.children.push(dirInfo);
            } else {
              dirMap.dirs.push(dirInfo);
            }
            loop(dirInfo, index + 1);
          }
        });
      };
      loop(null, 0);
      this.list = [...dirMap.dirs, ...dirMap.files];
    },
    // 关闭搜索：直接清除刷新
    closeSearch() {
      this.resetSearch();
      this.initTree();
    },
    // 取消搜索：保留已搜索到的列表，仅停止搜索
    cancelSearch() {
      this.isSearchDone = true;
      this.isSearching = false;
      eventProxy.emit('cancel-last-search');
    },
    // 重置搜索状态
    resetSearch() {
      // 清空搜索状态
      this.isSearching = false;
      this.isSearched = false;
      this.isSearchDone = false;
      this.keyword = '';
      this.showSearchBar = false;
      this.searchResultList.splice(0);
    },

    // 节点操作
    nodeClick(node) {
      this.clearContext();
      if (node.isLeaf) {
        this.$emit('update:fileItem', { key: node.key });
      }
    },
    contextmenuFn(event, node, nodeData) {
      // 当前只支持单选操作
      this.contextingNode = node;
      this.selectedList = nodeData ? [{ ...nodeData, id: node.id }] : [];
      this.menuType = node ? 'file' : 'bucket';
      this.dialogShow.contextMenu = true;
      this.contextMenuPosition = { x: event.clientX, y: event.clientY + 97 };
      const isBucket = !node;
      const isDir = node?.isDir;
      Object.assign(this.disabledBtn, {
        upload: !isBucket,
        download: isBucket,
        delete: isBucket,
        create: isBucket,
        copy: isBucket,
        paste: (!isBucket && !isDir) || !copyer.clipboard, // 文件上不能粘贴；没有复制项不能粘贴
        edit: isBucket || isDir,
        detail: true,
        preview: true,
        rename: isBucket || isDir,
        share: isBucket,
        uploadAndCreate: !isDir,
      });
      eventProxy.emit('context-menu-option', this.disabledBtn);
      event.preventDefault();
    },
    handleEventAction(action) {
      if (this.contextingNode) {
        this.fileContextFn(action);
      } else {
        this.bucketContextFn(action);
      }
    },
    bucketContextFn(action) {
      switch (action) {
        case 'uploadFile':
        case 'uploadFolder': {
          if (this.allowUploadMap[this.Region]) {
            const inputElStr = action === 'uploadFile' ? 'fileInput' : 'directoryInput';
            this.$refs[inputElStr].click();
          } else {
            tips.error(this.$t('error.curr_region_not_support_operator'));
          }
          break;
        }
        case 'refresh': {
          this.onRefresh();
          break;
        }
        default: {
          this.onEventAction({ action });
          break;
        }
      }
    },
    fileContextFn(action) {
      this.onEventAction({ action, item: this.selectedList[0] });
    },
    // 处理文件/文件夹上传
    handleFileOrFolderInput(e, type) {
      const inputElStr = type === 'file' ? 'fileInput' : 'directoryInput';
      const component = this.$refs.advanceUpload;
      component.initData();
      component.handleFileInput(e, this.$refs[inputElStr]);
      if (component.disableBtn) return;
      component.onSubmit();
    },
    // 表格/网格点击操作
    onEventAction({ action, item }) {
      switch (action) {
        case 'edit':
          this.$emit('update:fileItem', item);
          break;
        case 'download': {
          const singleFile = item || this.selectedList[0];
          if (this.selectedList.length === 1 && !singleFile.isDir) {
            this.onDownload(singleFile);
          } else {
            this.handleMultiDownload();
          }
          break;
        }
        case 'openFolderOrFile':
          this.openFolderOrFile(item);
          break;
        case 'delete':
          this.onDeleteFiles(item);
          break;
        case 'uploadFile':
        case 'uploadFolder': {
          if (this.allowUploadMap[this.Region]) {
            const inputElStr = action === 'uploadFile' ? 'fileInput' : 'directoryInput';
            this.$refs[inputElStr].click();
          } else {
            tips.error(this.$t('error.curr_region_not_support_operator'));
          }
          break;
        }
        case 'createFile':
        case 'createFolder':
          if (this.allowUploadMap[this.Region]) {
            const funcName = `on${action.replace(/^c/, 'C')}`;
            this[funcName]();
          } else {
            tips.error(this.$t('error.curr_region_not_support_operator'));
          }
          break;
        case 'openFiles':
          this.openFiles(item);
          break;
        case 'share':
        case 'fastCopyLink':
          if (!item.isDir) {
            const type = action === 'share' ? 'showDialog' : 'fastCopy';
            this.copyLinkOpen(item, type);
          } else {
            // 多文件分享分享
            this.openShareMultiple(item);
          }
          break;
        case 'rename':
          this.onRename(item);
          break;
        case 'copy':
          this.copyFiles(item);
          break;
        case 'paste':
          this.pasteFiles(item);
          break;
        default:
          break;
      }
    },
    // 上传成功
    async onUploadFinish(r) {
      if (!r.err) {
        const info = r.data;
        if (info.Bucket === this.Bucket && info.Region === this.Region) {
          let { Key } = info;
          if (!Key.startsWith(this.Prefix)) {
            Key = `${this.Prefix}${Key}`;
          }
          this.appendTreeNode(Key);
          tips.success(window.lang.t('core.sync_state_map.success'));
        }
      }
    },
    // 刷新桶
    onRefresh() {
      this.list.splice(0);
      this.Marker = '';
      this.hasMore = false;
      this.resetSearch();
      this.initTree();
    },
    onCreateFolder() {
      eventProxy.emit('create.show', 'folder');
    },
    onCreateFile() {
      eventProxy.emit('create.show', 'file');
    },
    async createFolderFn(name, isFolder) {
      this.appendTreeNode(name, isFolder);
    },
    // 删除文件
    onDeleteFiles() {
      const { Bucket, Region } = this;
      cosExpand.checkBucketReplication({ Bucket, Region }).then(data => {
        this.enableReplication = data;
        this.dialogShow.delete = true;
      });
    },
    deleteFn(list) {
      // 暂时只能删一个
      const node = this.$refs.tree.getNode(list[0]);
      if (!node) {
        return;
      }
      this.$refs.tree.removeNode(node);
      eventProxy.emit('command.delete', list);
    },
    // 删除文件夹内最后一个文件或者文件夹后，重新创建该文件夹
    fixFolder(path) {
      // 如果是最后一个文件（夹）
      if (!this.list.length && path !== '' && !this.hasMore) {
        // 创建当前文件夹
        this.$refs.create.onCreate(true);
      }
    },
    // 单个文件下载
    onDownload(item) {
      const { Bucket, Region } = this;
      const params = { Bucket, Region, Key: item.key };
      cosExpand
        .getObjectDownloadUrl(params)
        .then(async Url => {
          if (['DEEP_ARCHIVE', 'ARCHIVE'].includes(item.StorageClass)) {
            const restoreInfo = await cosExpand.getRestoreInfo(params);
            const storageText = this.$t(
              `cos.storage_class_map.${item.StorageClass}`,
            );
            if (restoreInfo.status === 'restored') {
              downloadUrl(Url);
            } else if (restoreInfo.status === 'restoring') {
              tips.error(
                this.$t('state.fail.download_with_restoring', { storageText }),
              );
            } else {
              tips.error(
                this.$t('state.fail.download_with_no_restore', { storageText }),
              );
            }
          } else {
            downloadUrl(Url);
          }
        })
        .catch(err => {
          tips.error(err);
        });
    },
    // 多文件下载
    handleMultiDownload() {
      const { Bucket, Region, selectedList } = this;
      this.dialogShow.openCosBrowser = false;
      this.dialogShow.reloadCosBrowser = false;
      // 检查用户是否打开了cosBrowser
      downloader.checkCosBrowserStatus(
        () => {
          // 已开启客户端
          this.dialogShow.loadCosBrowser = true;
          downloader
            .addDownloadTask({ Bucket, Region, selectedList })
            .then(() => {
              this.dialogShow.loadCosBrowser = false;
            })
            .catch(err => {
              this.dialogShow.loadCosBrowser = false;
              if (err === 'reload') {
                this.dialogShow.reloadCosBrowser = true;
              } else {
                tips.error(err.message || err);
              }
            });
        },
        () => {
          // 未开启客户端
          this.dialogShow.openCosBrowser = true;
        },
      );
    },
    // 重命名
    onRename(item) {
      let realPrefix = '';
      if (item) {
        this.selectedList = [item];
        realPrefix = pathLib.dirname(this.selectedList[0].key);
        realPrefix = realPrefix ? `${realPrefix}/` : realPrefix;
      }
      const { Bucket, Region } = this;
      const Prefix = realPrefix;
      const source = {
        Bucket,
        Region,
        Prefix,
        files: this.selectedList,
      };
      const target = { ...this.bucketInfo, Prefix };
      eventProxy.emit('show.rename', { source, target, action: 'rename' });
    },
    // 重命名结束后根据情况进行列表的刷新
    onRenameFinish(item) {
      const { isSearched } = this;
      if (isSearched) {
        // 模糊搜索时保留搜索列表
        // 1. 移除原来的文件
        this.list = this.list.filter(o => o.key !== item.key);
        // 2.若新名字在搜索关键字中再将新名字放入列表里
        if (item.newKey.includes(this.realKeyword)) {
          item.key = item.newKey;
          item.name = item.key.substr(item.key.lastIndexOf('/') + 1);
          item.mtime = '-';
          this.list.push(item);
        }
      } else {
        this.onRefresh();
      }

      // 重命名结束后剪辑板为空，禁用粘贴
      this.disabledBtn.paste = true;
      eventProxy.emit('context-menu-option', this.disabledBtn);
    },
    // 打开多文件分享弹窗
    openShareMultiple(item) {
      if (item) {
        this.selectedList = [item];
      }
      const { Bucket, Region } = this;
      cosExpand.checkBucketReplication({ Bucket, Region }).then(data => {
        this.enableReplication = data;
        this.dialogShow.shareMultiple = true;
      });
    },
    // 多文件分享
    async handleShareMultiple({ DurationSeconds, extract, permission }) {
      const files = this.selectedList;
      if (!files.length) return;
      tips.loading(this.$t('core.loading'));
      const { Bucket, Region, Prefix } = this;
      const isMaz = this.bucketInfo.MAZConfig === 'MAZ' ? 1 : 0;
      try {
        const res = await shareMultipleTools.getShareLink({
          Bucket,
          Region,
          Prefix,
          isMaz,
          files,
          DurationSeconds,
          extract,
          permission,
        });
        tips.closeTips();
        this.shareMultipleInfo = res;
        this.dialogShow.shareMultiple = false;
        this.dialogShow.shareResult = true;
      } catch (error) {
        tips.closeTips();
        tips.error(error);
      }
    },
    // 打开单文件复制链接弹窗
    async copyLinkOpen(item, type) {
      if (item) this.selectedList = [item];

      this.dialogShow.fileInfo = false;
      const file = item || this.selectedList[0];
      if (!file) return;

      let isPublic = '';

      const { Bucket, Region } = this;
      const { acl: bucketAcl } = this.bucketInfo;
      const params = {
        Bucket,
        Region,
        bucketAcl,
        Key: file.key,
      };

      const handleInfo = acl => {
        this.shareFileInfo = {
          name: file.name,
          key: file.key,
          acl,
          type: isPublic ? 'unSign' : 'sign',
          url: file.url,
        };
        // 文件列表 icon 是快速复制链接
        // 文件详情里的“创建临时链接”是弹窗复制链接
        // 其他根据是否公有读判断
        if (type === 'fastCopy') {
          this.shareFileInfo.tag = 'fastCopy';
          this.$refs.shareFile.doShareLink(this.shareFileInfo);
        }
      };

      if (type !== 'fastCopy') {
        this.dialogShow.shareFile = true;
        handleInfo('loading');
      } else {
        tips.loading(window.lang.t('cos.loading_file_acl'));
      }

      // 设定 3 秒 获取真实 acl 请求超时时间
      setTimeout(() => {
        if (isPublic === '') {
          tips.closeTips();
          tips.error(this.$t('error.loading_file_acl_timeout'));
          isPublic = false;
          if (type === 'fastCopy') {
            type = 'showDialog';
            this.dialogShow.shareFile = true;
          }
          handleInfo('unknown-with-timeout');
        }
      }, 3000);

      const realACL = await cosExpand.getObjectRealAcl(params);

      tips.closeTips();
      if (isPublic !== '') return;
      isPublic = cosSdkUtils.isPublic(realACL);
      handleInfo(realACL);
    },
    // 复制文件/文件夹
    copyFiles(list) {
      list = list && list.length ? list : this.selectedList;
      const copyCount = list.length;
      if (copyCount) {
        const source = {
          ...this.bucketInfo,
          Prefix: this.Prefix,
          files: list,
        };
        copyer.copy(source, () => {
          tips.closeTips();
          tips.success(
            window.lang.t('cos.copy_files.msg', { count: copyCount }),
          );
        });
        this.disabledBtn.paste = false;
        eventProxy.emit('context-menu-option', this.disabledBtn);
        navigator.clipboard.writeText('');
      }
    },
    /**
     * 粘贴文件/文件夹，弹出确认弹窗
     */
    async pasteFiles(opt) {
      const e = window.event;
      const clip = e && e.clipboardData;
      if (clip && clip.files && clip.files.length) {
        this.uploadPasteFiles({ files: clip.files, paste: true });
        copyer.clipboard = null;
      } else if (copyer.clipboard) {
        const prefix = this.contextingNode?.key || '';
        const source = copyer.clipboard;
        let target = { ...this.bucketInfo, Prefix: prefix };
        let action = 'copy';
        if (opt && ['copy', 'move'].includes(opt.action)) {
          ({ target, action } = opt);
        }

        if (source.files.length === 1 && !source.files[0].isDir) {
          eventProxy.emit('show.rename', { source, target, action });
        } else {
          const { sameNameList, code } = await copyer.getSameNameList(
            source,
            target,
          );
          if (code) {
            tips.error(this.$t(`error.${code}`));
          } else if (sameNameList) {
            if (!sameNameList.length) {
              this._pasteFiles({ target, action });
            } else {
              eventProxy.emit('show.pasteFiles', {
                source,
                target,
                action,
                sameNameList,
              });
            }
          }
        }
      } else {
        tips.error(this.$t('error.no_file_to_paste'));
      }
    },
    // 复制粘贴 | 复制移动文件/文件夹，真正粘贴
    _pasteFiles(opt) {
      const { target, action } = opt;
      tips.loading(
        this.$t('core.state_map.doing[0]', {
          action: this.$t(`core.${action}`),
        }),
      );
      if (action === 'copy') {
        target.allowSituCopy = true; // 允许原地复制粘贴
      }
      target.onProgress = info => {
        const combineText = `${this.$t(`core.${action}`)} (${info.fileLoaded}/${
          info.fileTotal
        })，${info.Key} (${Math.round(info.percent * 10000) / 100}%)`;
        tips.loading(
          this.$t('core.state_map.doing[0]', { action: combineText }),
        );
      };
      target.action = action;

      copyer[`${action}To`](target, (err, data) => {
        if (data) {
          if (data.successList.length) {
            tips.success(
              this.$t(`state.success.${action}_files`, {
                count: data.successList.length,
              }),
            );
          }
          setTimeout(() => {
            this.onRefresh();
          }, 500);
        }

        // 移动| 粘贴结束后剪辑板为空，禁用粘贴按钮
        if (action === 'move') {
          this.disabledBtn.paste = true;
          eventProxy.emit('context-menu-option', this.disabledBtn);
        }
      });
    },
    // 加载子节点
    loadNode(node) {
      return new Promise(resolve => {
        this.getTreeData(node.key).then(res => {
          if (!node.children) {
            this.$set(node, 'children', []);
          }
          node.children.push(...res.list);
          resolve({
            nodes: res.list,
            hasMore: res.hasMore,
          });
        });
      });
    },
    // 手动添加节点
    appendTreeNode(name, isFolder = false) {
      const { Prefix } = this;
      if (!name.startsWith(Prefix)) {
        return;
      }
      name = name.replace(Prefix, '');
      const path = name.split('/').filter(Boolean);
      const { existPath, needCreatePath, node } = this.getClosestPath(path);
      const parentPath = existPath.length > 0 ? existPath.map(item => `${item}/`).join('') : '';
      let prefix = Prefix + parentPath;
      let newNode = {};
      if (needCreatePath.length === 1 && !isFolder) {
        prefix += `${needCreatePath[0]}`;
        newNode = this.formatFileItem({ Key: prefix });
      } else {
        prefix += `${needCreatePath[0]}/`;
        newNode = this.formatFileItem({ Prefix: prefix });
      }

      if (node) {
        const nodeIns = this.$refs.tree.getNode(node);
        if (nodeIns.loaded) {
          nodeIns.data.children.push(newNode);
          this.sortList(nodeIns.data.children);
        }
      } else {
        this.list.push(newNode);
        this.sortList(this.list);
      }
    },
    getClosestPath(pathArr) {
      let node = null;
      const needCreatePath = [...pathArr];
      const existPath = [];
      const loop = (list, index) => {
        const path = pathArr[index];
        const child = list.find(item => item.label === path);
        if (child) {
          existPath.push(path);
          needCreatePath.shift();
          node = child;
          if (child.children) {
            return loop(child.children, index + 1);
          }
        }
        return { existPath, needCreatePath, node };
      };
      return loop(this.list, 0);
    },
    toStandardMode() {
      const routeUrl = this.$router.resolve({
        name: 'file',
        query: this.$route.query,
      });
      window.open(routeUrl.href, '_blank');
    },
    logout() {
      eventProxy.emit('command.logout');
    },
    // 清除节点右键选中
    clearContext() {
      if (this.contextingNode) {
        this.contextingNode.isContexting = false;
        this.contextingNode = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.logo-container {
  flex: none;
}
.operate-content {
  border-top: 1px solid #4c4c4c;
  padding: 10px 20px;
  ul > li {
    margin-bottom: 10px;
    a {
      padding: 5px;
      color: rgba(211, 222, 235, 0.9);
    }
  }
}
.tree-search-content {
    display: flex;
    margin-bottom: 10px;
    padding: 0 10px;
    box-sizing: border-box;
    &>.tc-15-search {
      flex: auto;
      display: flex;
    }
    .search-input-wrap {
      display: inline-flex;
      flex: auto;
      input {
        width: 100%;
        padding: 0 10px;
      }
    }
    .tc-15-btn:hover {
      background: #2c2c2d;
    }
    .tc-15-btn.refresh {
      width: 30px;
      height: 30px;
      background: #1f1f21;
    }
    .tc-15-btn.refresh:hover {
      background: #0c0c0c;
    }
}
.search-bar {
  flex: none;
  height: 26px;
  padding: 0;
  margin: 0;
  .justify-grid {
    height: 100%;
    background: #434444;
    padding: 0 10px;
    box-sizing: border-box;
    .search-text,
    .cancel-search {
      font-size: 12px;
    }
  }
}
.tree-content {
  flex: auto;
  overflow: auto;
}
.dark-theme {
  .tree-content {
    ::v-deep .qc-tree-node-content {
      color: rgba(211, 222, 235, 0.9);
    }
    ::v-deep .qc-tree-node-content:hover {
      background: #525458;
    }
    ::v-deep .qc-tree-node-content.is-contexting {
      background: #39393a;
    }
    ::v-deep .qc-tree-node-content.is-current {
      background: #525458;
    }
    ::v-deep .cos-icon--32 {
      width: 16px;
      height: 16px;
    }
  }
}
</style>
